// The live sky that Omni's sign-in screens and phoneware.us both draw: sky.jpg's sky (azure overhead, paler toward the
// horizon, the sun's glow and faint rays from the top right) painted by a fragment shader, with soft clouds drifting
// slowly through a bank low on one side. On phoneware.us it fills the home hero, the home testimonial and the CTA band
// at the foot of nearly every page.
//
// Every page load draws its own sky: where in the cloud noise it begins, which side the bank gathers on, how high it
// climbs and how much cloud it holds. It is drawn the first time a page's sky is built and kept until the page goes,
// so the clouds never change while someone moves between screens.
//
// sky.jpg (`@phoneware/sky/sky.jpg`) stays underneath, as the page's own first paint, centred and cropped to cover
// the way the shader crops its frame. It is what people see wherever WebGL is missing, fails to build or is lost: the
// canvas is taken away rather than ever showing a black or empty sky. The canvas gets the class `live` once it has
// drawn, so the page can keep it transparent until then and fade it in over the photo.
//
// A page's sky has one canvas, which every host it lends to shares, so moving between screens hands the same sky to
// the next host instead of rebuilding it. A page that shows several skies at once, as phoneware.us's home page does,
// makes one for each: each has its own canvas and WebGL context, built only once its host comes within a screen of the
// window, so a band at the foot of a page costs nothing until someone scrolls toward it. A sky draws only while its
// host is on screen, apart from the one still frame it draws when it is built so that it has faded in before anyone
// reaches it, and every sky on the page draws in the same frames, so however many are on screen the page changes no
// more than 30 times a second. Time is the wall clock, so a sky rebuilt later in the same page picks the drift up where
// it would have been.

/** A page opts into night explicitly; the renderer never reads OS or application theme state. */
export type SkyAppearance = "day" | "night";

const VERTEX = `
attribute vec2 aPosition;
varying vec2 vUv;
void main() {
  vUv = vec2(aPosition.x * 0.5 + 0.5, 0.5 - aPosition.y * 0.5);
  gl_Position = vec4(aPosition, 0.0, 1.0);
}`;

const FRAGMENT = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif

varying vec2 vUv;
uniform vec4 uView;     // canvas to scene: uv = uView.xy + vUv * uView.zw, y down
uniform vec2 uWind;     // the clouds' drift
uniform vec2 uCurl;     // drift of the field that bends them, so they change shape as they go
uniform vec2 uWisp;     // drift of the fine streaks and the horizon haze
uniform vec4 uBank;     // this visit's clouds: the bank's centre and half width across the canvas, its top edge, how much cloud
uniform float uShimmer; // the rays' slow brighten and fade, 0 to 2 pi
uniform float uNight;   // appearance only; switching never changes the cloud field or its drift
uniform vec2 uSize;     // backing-store pixels, so stars remain fine points in either crop

const float ASPECT = 1.7937;            // sky.jpg is 1600 x 892
const float PERIOD = 32.0;              // the noise repeats here, so the drifts wrap without a seam
const vec2 SUN = vec2(0.9975, -0.0825); // fitted to the photo's glow, just above the top right corner

vec2 gradient(vec2 cell) {
  vec3 p = fract(vec3(mod(cell, PERIOD).xyx) * vec3(0.1031, 0.1030, 0.0973));
  p += dot(p, p.yzx + 33.33);
  return fract((p.xx + p.yz) * p.zy) * 2.0 - 1.0;
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = p - i;
  vec2 u = f * f * f * (f * (f * 6.0 - 15.0) + 10.0);
  float a = dot(gradient(i), f);
  float b = dot(gradient(i + vec2(1.0, 0.0)), f - vec2(1.0, 0.0));
  float c = dot(gradient(i + vec2(0.0, 1.0)), f - vec2(0.0, 1.0));
  float d = dot(gradient(i + vec2(1.0, 1.0)), f - vec2(1.0, 1.0));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

// Octaves double exactly, so every layer keeps the period.
float fbm3(vec2 p) {
  float s = 0.0;
  float a = 0.5;
  for (int i = 0; i < 3; i++) {
    s += a * noise(p);
    p = p * 2.0 + vec2(5.3, 9.1);
    a *= 0.5;
  }
  return s;
}

float fbm6(vec2 p) {
  float s = 0.0;
  float a = 0.5;
  for (int i = 0; i < 6; i++) {
    s += a * noise(p);
    p = p * 2.0 + vec2(5.3, 9.1);
    a *= 0.5;
  }
  return s;
}

float hash1(float n) {
  return fract(sin(n * 12.9898) * 43758.5453);
}

float noise1(float x) {
  float i = floor(x);
  float f = x - i;
  f = f * f * (3.0 - 2.0 * f);
  return mix(hash1(i), hash1(i + 1.0), f);
}

// The photo's sky without its clouds: a gradient fitted down its left edge, then
// the sun's broad warm glare and its brighter core.
vec3 sky(vec2 uv) {
  float y = uv.y;
  vec3 c = vec3(0.1908, 0.5953, 0.8029)
    + y * (vec3(0.5720, 0.4150, 0.2579)
    + y * (vec3(-0.5257, -0.5874, -0.3619)
    + y * (vec3(1.8223, 1.4627, 0.8567)
    + y * vec3(-1.2686, -1.0288, -0.6805))));
  vec2 d = (uv - SUN) * vec2(ASPECT, 1.0);
  vec2 e = d / vec2(0.6656, 0.5531);
  c += vec3(0.7262, 0.1794, -0.0975) * exp(-dot(e, e));
  c += vec3(0.1381, 0.3250, 0.3556) * exp(-length(d) / 0.1994);
  return c;
}

// The same scene at night: deep blue overhead, a restrained horizon, and static stars behind the clouds.
vec3 nightSky(vec2 uv, vec2 p) {
  vec3 c = mix(vec3(0.0196, 0.0471, 0.0863), vec3(0.0667, 0.1333, 0.1922), clamp(uv.y, 0.0, 1.0));
  vec2 cell = floor(p * 24.0);
  vec2 random = gradient(cell * 0.67) * 0.5 + 0.5;
  vec2 centre = gradient(cell * 0.67 + vec2(11.3, 4.7)) * 0.32 + 0.5;
  vec2 pixel = uView.zw * vec2(ASPECT, 1.0) * 24.0 / uSize;
  float d = length((fract(p * 24.0) - centre) / pixel);
  float point = (1.0 - smoothstep(0.15, 0.8, d)) * step(0.925, random.x);
  float brightness = mix(0.35, 0.8, random.y);
  c += vec3(0.50, 0.61, 0.72) * point * brightness * (1.0 - smoothstep(0.65, 0.98, uv.y));
  return c;
}

// Faint streaks fanning from the sun toward the lower left.
float rays(vec2 uv) {
  vec2 d = (uv - SUN) * vec2(ASPECT, 1.0);
  float r = length(d);
  float a = atan(d.y, -d.x);
  float x = a * 11.0;
  float one = noise1(x + 3.7) * 0.75 + noise1(x * 2.1 + 17.0) * 0.25;
  float two = noise1(x + 41.0) * 0.75 + noise1(x * 2.1 + 59.0) * 0.25;
  float streak = smoothstep(0.2, 1.0, mix(one, two, 0.5 + 0.5 * sin(uShimmer)));
  float fan = smoothstep(-0.12, 0.12, a) * (1.0 - smoothstep(1.05, 1.5, a));
  return streak * fan * smoothstep(0.15, 0.65, r) * exp(-0.7 * r);
}

// Where this visit's clouds gather: a span of the lower sky that widens toward the horizon. It is measured across
// the canvas rather than the scene, so a phone's narrow slice of the sky still holds its bank beside the card.
float region(vec2 uv) {
  float across = 1.0 - smoothstep(uBank.y - 0.13, uBank.y + 0.13, abs(vUv.x - uBank.x) - 0.10 * (uv.y - 0.6));
  float band = smoothstep(uBank.z - 0.08, uBank.z + 0.08, uv.y) * (1.0 - smoothstep(0.86, 0.96, uv.y));
  return across * band;
}

// Above zero is cloud: drifting fbm bent by a second, slower field, frayed by fine fibres along the wind.
float shape(vec2 p, float r) {
  vec2 q = p * vec2(2.6, 6.0);
  vec2 bend = vec2(fbm3(q * 0.5 + uCurl), fbm3(q * 0.5 + uCurl + vec2(11.3, 4.7))) * vec2(0.9, 0.5);
  float n = fbm6(q + bend + uWind);
  n += 0.09 * fbm3(p * vec2(8.0, 36.0) + 1.6 * bend + uWisp);
  return n + uBank.w * r - 0.30;
}

void main() {
  vec2 uv = uView.xy + vUv * uView.zw;
  vec2 p = uv * vec2(ASPECT, 1.0);
  vec3 col;
  if (uNight > 0.5) {
    col = nightSky(uv, p);
  } else {
    col = sky(uv);
  col += vec3(1.0, 0.88, 0.74) * 0.11 * rays(uv);
  // The pale band of haze along the horizon, in faint streaks.
  float h = (uv.y - 0.945) / 0.03;
  col += vec3(0.034, 0.02, 0.012) * exp(-h * h) * (0.7 + 2.0 * fbm3(vec2(p.x * 2.0, p.y * 60.0) + uWisp));
  }
  float r = region(uv);
  float s = shape(p, r);
  float edge = smoothstep(0.0, 0.2, r);
  // A thin veil around and between the clouds, then their bodies.
  float veil = smoothstep(-0.09, 0.02, s) * 0.22 * edge;
  float body = smoothstep(0.0, 0.06, s) * edge;
  if (veil > 0.0) {
    col = mix(col, uNight > 0.5 ? vec3(0.22, 0.32, 0.40) : vec3(0.9, 0.94, 0.95), veil);
    if (body > 0.0) {
      // Whitest where thick and facing the sun; a little bluer away from it, and thin edges let the sky through.
      vec2 toSun = normalize((SUN - uv) * vec2(ASPECT, 1.0));
      float lit = clamp(0.5 + 3.5 * (s - shape(p + toSun * 0.035, r)), 0.0, 1.0);
      float thick = smoothstep(0.03, 0.2, s);
      vec3 tone;
      if (uNight > 0.5) {
        tone = mix(vec3(0.25, 0.36, 0.44), vec3(0.13, 0.21, 0.27), (1.0 - lit) * (0.3 + 0.6 * thick));
      } else {
        tone = mix(vec3(0.975, 0.975, 0.95), vec3(0.72, 0.83, 0.89), (1.0 - lit) * (0.3 + 0.6 * thick));
      }
      col = mix(col, tone, body * mix(0.55, 0.98, thick));
    }
  }
  // Dither, so the long gradients never band.
  col += (fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453) - 0.5) / 255.0;
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
}`;

/** sky.jpg's frame. The shader crops it the way `background-size: cover` crops the photo, so the two line up. */
const SCENE_ASPECT = 1600 / 892;
/** Soft clouds survive a small backing store: at most half the CSS size, and under half a megapixel. */
const RENDER_SCALE = 0.5;
const MAX_PIXELS = 480_000;
/** Drift this slow needs no more than 30 frames a second. */
const FRAME_MS = 1000 / 30 - 2;
/** How near the window a host further down the page has to come before its sky is built, in screen heights. */
const NEAR = 1;
/** The shader's noise period. Each drift is wrapped by it here, in double precision, so floats never run out. */
const NOISE_PERIOD = 32;
// Drift in noise units a second. The clouds move right at about 0.28% of the sky's height a second (2.5 px on a
// 900 px window); the field that bends them, and the fine wisps, move slower, so their shapes change as they go.
const WIND = [-0.0084, 0.0007] as const;
const CURL = [-0.0031, 0.0012] as const;
const WISP = [-0.0144, 0.0003] as const;
/** The rays brighten and fade over this many seconds. */
const SHIMMER_SECONDS = 47;

// The ranges each page's sky is drawn from, chosen by rendering hundreds of random skies behind the card and
// rejecting the tails. The bank always gathers low on the left or the right, never in the middle behind the card.
/** How far toward the middle a right-hand bank's inner edge reaches, in canvas widths; a left-hand bank mirrors it.
 *  Nearer the middle the bank crowds around the card; further out it retreats to the edge of the frame and can leave
 *  the narrow sky of a phone or a tablet all but empty. */
const INNER = [0.47, 0.57] as const;
/** The bank's top edge, down the scene (sky.jpg's is 0.42). Higher, it climbs the whole height of the card's sides;
 *  lower, it thins to a strip along the horizon. */
const TOP = [0.36, 0.56] as const;
/** How much cloud fills the bank (sky.jpg's is 0.36). Less leaves some skies all but empty; more turns the bank heavy. */
const CLOUD = [0.33, 0.41] as const;

const CONTEXT: WebGLContextAttributes = {
  alpha: true,
  antialias: false,
  depth: false,
  stencil: false,
  premultipliedAlpha: true,
  preserveDrawingBuffer: false,
  powerPreference: "low-power",
  // A decoration is not worth a software renderer's CPU: the photo stands in instead.
  failIfMajorPerformanceCaveat: true,
};

/**
 * The frame every sky on the page last drew in. The first sky to reach the next beat starts it and the rest draw in the
 * same frame, so several skies on screen still change the page no more than 30 times a second.
 */
let beat = -Infinity;

function wrap(value: number): number {
  return ((value % NOISE_PERIOD) + NOISE_PERIOD) % NOISE_PERIOD;
}

/** The part of the scene a `cover` crop shows at this size: x, y, width, height, in scene units. */
function cover(width: number, height: number): [number, number, number, number] {
  if (width <= 0 || height <= 0) return [0, 0, 1, 1];
  const aspect = width / height;
  if (aspect > SCENE_ASPECT) {
    const h = SCENE_ASPECT / aspect;
    return [0, (1 - h) / 2, 1, h];
  }
  const w = aspect / SCENE_ASPECT;
  return [(1 - w) / 2, 0, w, 1];
}

/** Whether `host` is on screen, or within `margin` screen heights above or below it. */
function onScreen(host: HTMLElement, margin = 0): boolean {
  const box = host.getBoundingClientRect();
  const reach = window.innerHeight * margin;
  return box.bottom >= -reach && box.top <= window.innerHeight + reach && box.right >= 0 && box.left <= window.innerWidth;
}

/** How one page load's sky differs from every other's. */
interface Visit {
  /** Where the clouds, the field that bends them and the wisps begin in the noise: x then y for each, inside the period. */
  readonly start: readonly number[];
  /** The shader's uBank: the bank's centre and half width across the canvas, its top edge, and how much cloud. */
  readonly bank: readonly [number, number, number, number];
}

/** Where a page's cloud bank gathers, and what it has to keep clear of. */
export interface Placement {
  /** The side the bank gathers on; "either" draws one for each page load, as Omni's sign-in does around its card. */
  readonly side: "either" | "left" | "right";
  /**
   * Boxes, in viewport pixels, that no cloud may sit behind, read every time the sky draws so they follow the page as
   * it reflows. Where the page's own bank would reach one, the bank's inner edge moves out or its top edge moves down,
   * whichever leaves more of it, until none of them has any cloud or veil behind it.
   */
  readonly clear?: () => Iterable<DOMRectReadOnly>;
}

/** Omni's sign-in card sits in the middle, so its bank may gather on either side. */
const EITHER: Placement = { side: "either" };

/** A new sky, drawn from the browser's randomness. */
function roll(placement: Placement): Visit {
  const [wx, wy, cx, cy, sx, sy, side, reach, height, amount] = Array.from(crypto.getRandomValues(new Uint32Array(10)), (n) => n / 2 ** 32);
  const pick = ([low, high]: readonly [number, number], t: number) => low + (high - low) * t;
  const right = placement.side === "either" ? side < 0.5 : placement.side === "right";
  const inner = pick(INNER, reach);
  const top = pick(TOP, height);
  // A lower bank shows less of itself, so it gets a little more cloud, and one on the left stands out against the
  // deeper blue, so a little less: whichever side and height a page draws, its sky carries about the same weight.
  const cloud = pick(CLOUD, amount) + 0.35 * (top - 0.46) - (right ? 0 : 0.03);
  return {
    start: [wx, wy, cx, cy, sx, sy].map((t) => t * NOISE_PERIOD),
    // A centre this far out keeps the bank's outer edge off the canvas, and the half width puts its inner edge at
    // `inner`, or at the mirror of it.
    bank: [right ? 1.3 : -0.3, 1.3 - inner, top, cloud],
  };
}

// Where the shader's region() is exactly zero, so no cloud and no veil: further than EDGE past the bank's half width
// across the canvas, less SPREAD for each scene height below 0.6, or above where its band starts to feather in, or
// past the horizon.
const EDGE = 0.13;
const SPREAD = 0.1;
const FEATHER = 0.08;
const HORIZON = 0.96;

/** A box to keep clear, in the shader's terms: across the canvas from 0 to 1, and down the scene. */
interface Box {
  readonly x0: number;
  readonly x1: number;
  readonly y0: number;
  readonly y1: number;
}

/** The placement's boxes that fall on the host, in the shader's terms for this frame's crop of the scene. */
function boxesIn(host: HTMLElement, view: readonly number[], clear: () => Iterable<DOMRectReadOnly>): Box[] {
  const frame = host.getBoundingClientRect();
  if (frame.width <= 0 || frame.height <= 0) return [];
  const boxes: Box[] = [];
  for (const rect of clear()) {
    const x0 = Math.max(0, (rect.left - frame.left) / frame.width);
    const x1 = Math.min(1, (rect.right - frame.left) / frame.width);
    const y0 = Math.max(0, (rect.top - frame.top) / frame.height);
    const y1 = Math.min(1, (rect.bottom - frame.top) / frame.height);
    if (x1 <= x0 || y1 <= y0) continue;
    boxes.push({ x0, x1, y0: view[1] + y0 * view[3], y1: view[1] + y1 * view[3] });
  }
  return boxes;
}

/**
 * The bank a frame draws: the page's own, or, where that would put cloud behind a box, the nearest one that keeps
 * every box clear by moving the inner edge out, the top edge down, or both, whichever leaves the most of the bank.
 */
function placeBank(bank: Visit["bank"], boxes: readonly Box[]): Visit["bank"] {
  const right = bank[0] > 0.5;
  // In a right-hand bank's terms, as roll() draws them: the inner edge across the canvas, the top edge down the scene.
  const inner = 1.3 - bank[1];
  const top = bank[2];
  // What each box asks of the bank: an inner edge at least this far out, or a top edge at least this low.
  const asks = boxes
    .filter((box) => box.y0 < HORIZON)
    .map((box) => ({
      inner: (right ? box.x1 : 1 - box.x0) + EDGE + SPREAD * (Math.min(box.y1, HORIZON) - 0.6),
      top: box.y1 + FEATHER,
    }));
  let best = { inner, top, room: -1 };
  for (const edge of [top, ...asks.map((ask) => ask.top).filter((low) => low > top)]) {
    const reach = Math.max(inner, ...asks.filter((ask) => ask.top > edge).map((ask) => ask.inner));
    const room = Math.max(0, 1 + EDGE - reach) * Math.max(0, HORIZON + FEATHER - edge);
    if (room > best.room) best = { inner: reach, top: edge, room };
  }
  if (best.inner === inner && best.top === top) return bank;
  // A lower bank shows less of itself, so it keeps the extra cloud roll() gives a bank drawn that low.
  return [bank[0], 1.3 - best.inner, best.top, bank[3] + 0.35 * (best.top - top)];
}

interface Uniforms {
  view: WebGLUniformLocation | null;
  wind: WebGLUniformLocation | null;
  curl: WebGLUniformLocation | null;
  wisp: WebGLUniformLocation | null;
  bank: WebGLUniformLocation | null;
  shimmer: WebGLUniformLocation | null;
  night: WebGLUniformLocation | null;
  size: WebGLUniformLocation | null;
}

class Sky {
  readonly canvas: HTMLCanvasElement;
  private readonly gl: WebGLRenderingContext;
  private readonly visit: Visit;
  private readonly placement: Placement;
  private readonly gone: (sky: Sky) => void;
  private readonly vertex: WebGLShader;
  private readonly fragment: WebGLShader;
  private readonly program: WebGLProgram;
  private readonly parallel: KHR_parallel_shader_compile | null;
  private readonly motion: MediaQueryList | null;
  private readonly resize: ResizeObserver | null;
  private readonly watch: IntersectionObserver | null;
  private buffer: WebGLBuffer | null = null;
  private uniforms: Uniforms | null = null;
  private checking = 0;
  private frame = 0;
  private live = false;
  /** Whether the host is on screen: read from the page when a host takes the sky, then kept by the observer. */
  private seen = true;
  private disposed = false;
  private owner: object | null = null;
  private host: HTMLElement | null = null;
  private onFail: (() => void) | null = null;
  private appearance: SkyAppearance;

  constructor(canvas: HTMLCanvasElement, gl: WebGLRenderingContext, visit: Visit, placement: Placement, gone: (sky: Sky) => void, appearance: SkyAppearance) {
    this.canvas = canvas;
    this.gl = gl;
    this.visit = visit;
    this.placement = placement;
    this.gone = gone;
    this.appearance = appearance;
    const vertex = gl.createShader(gl.VERTEX_SHADER);
    const fragment = gl.createShader(gl.FRAGMENT_SHADER);
    const program = gl.createProgram();
    if (!vertex || !fragment || !program) throw new Error("WebGL could not make the sky's program");
    gl.shaderSource(vertex, VERTEX);
    gl.compileShader(vertex);
    gl.shaderSource(fragment, FRAGMENT);
    gl.compileShader(fragment);
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    this.vertex = vertex;
    this.fragment = fragment;
    this.program = program;
    // Lets the driver build the program off the main thread, so the page is never kept waiting.
    this.parallel = gl.getExtension("KHR_parallel_shader_compile");
    this.motion = typeof window.matchMedia === "function" ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;
    this.resize = typeof ResizeObserver === "function" ? new ResizeObserver(() => this.seen && this.draw()) : null;
    // Says when the host comes on screen and when it leaves, so a sky nobody can see draws nothing.
    this.watch = typeof IntersectionObserver === "function" ? new IntersectionObserver(this.look) : null;
    canvas.addEventListener("webglcontextlost", this.fail);
    this.checking = window.setTimeout(this.check, 0);
  }

  setAppearance(appearance: SkyAppearance): void {
    if (this.appearance === appearance) return;
    this.appearance = appearance;
    this.play();
  }

  /** Shows the sky in a host. Returns the token that lets that host, and only that host, give it back. */
  attach(host: HTMLElement, onFail?: () => void): object {
    this.unhook();
    const owner = {};
    this.owner = owner;
    this.host = host;
    this.onFail = onFail ?? null;
    host.appendChild(this.canvas);
    this.motion?.addEventListener("change", this.play);
    document.addEventListener("visibilitychange", this.play);
    this.seen = onScreen(host);
    this.watch?.observe(host);
    this.resize?.observe(host);
    this.play();
    return owner;
  }

  detach(owner: object): void {
    if (this.owner !== owner) return;
    this.unhook();
    // A screen change hands the sky to the next host in the same commit. Only a page with no host lets it go.
    queueMicrotask(() => {
      if (!this.owner) this.dispose();
    });
  }

  private unhook(): void {
    this.stop();
    this.motion?.removeEventListener("change", this.play);
    document.removeEventListener("visibilitychange", this.play);
    this.resize?.disconnect();
    this.watch?.disconnect();
    this.canvas.remove();
    this.owner = null;
    this.host = null;
    this.onFail = null;
  }

  private dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.unhook();
    window.clearTimeout(this.checking);
    this.canvas.removeEventListener("webglcontextlost", this.fail);
    const { gl } = this;
    if (!gl.isContextLost()) {
      gl.deleteBuffer(this.buffer);
      gl.deleteProgram(this.program);
      gl.deleteShader(this.vertex);
      gl.deleteShader(this.fragment);
      // Hands the context back now rather than whenever the canvas is collected.
      gl.getExtension("WEBGL_lose_context")?.loseContext();
    }
    this.gone(this);
  }

  private readonly fail = (): void => {
    const onFail = this.onFail;
    this.dispose();
    onFail?.();
  };

  private readonly check = (): void => {
    this.checking = 0;
    const { gl, program } = this;
    if (this.disposed || gl.isContextLost()) return;
    if (this.parallel && !gl.getProgramParameter(program, this.parallel.COMPLETION_STATUS_KHR)) {
      this.checking = window.setTimeout(this.check, 16);
      return;
    }
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      this.fail();
      return;
    }
    gl.useProgram(program);
    this.buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
    // One triangle that covers the whole canvas.
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const position = gl.getAttribLocation(program, "aPosition");
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    this.uniforms = {
      view: gl.getUniformLocation(program, "uView"),
      wind: gl.getUniformLocation(program, "uWind"),
      curl: gl.getUniformLocation(program, "uCurl"),
      wisp: gl.getUniformLocation(program, "uWisp"),
      bank: gl.getUniformLocation(program, "uBank"),
      shimmer: gl.getUniformLocation(program, "uShimmer"),
      night: gl.getUniformLocation(program, "uNight"),
      size: gl.getUniformLocation(program, "uSize"),
    };
    this.play();
  };

  /**
   * Draws now, then keeps drawing unless motion is reduced. Off screen it draws only its first frame, so it has faded
   * in before anyone reaches it, and then nothing until its host comes back. Nothing at all while the tab is hidden.
   */
  private readonly play = (): void => {
    this.stop();
    if (!this.uniforms || !this.host || document.visibilityState === "hidden") return;
    if (!this.seen) {
      if (!this.live) this.draw();
      return;
    }
    this.draw();
    beat = performance.now();
    if (!this.motion?.matches) this.frame = window.requestAnimationFrame(this.tick);
  };

  private readonly look = (entries: IntersectionObserverEntry[]): void => {
    const seen = entries[entries.length - 1]?.isIntersecting ?? this.seen;
    if (seen === this.seen) return;
    this.seen = seen;
    this.play();
  };

  private readonly tick = (time: number): void => {
    this.frame = window.requestAnimationFrame(this.tick);
    if (time !== beat) {
      if (time - beat < FRAME_MS) return;
      beat = time;
    }
    this.draw();
  };

  private stop(): void {
    if (this.frame) window.cancelAnimationFrame(this.frame);
    this.frame = 0;
  }

  private draw(): void {
    const { gl, canvas, host, uniforms } = this;
    if (!uniforms || !host || gl.isContextLost()) return;
    const width = host.clientWidth;
    const height = host.clientHeight;
    const scale = Math.min(RENDER_SCALE, Math.sqrt(MAX_PIXELS / Math.max(1, width * height)));
    const w = Math.max(1, Math.round(width * scale));
    const h = Math.max(1, Math.round(height * scale));
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    gl.viewport(0, 0, w, h);
    const seconds = Date.now() / 1000;
    const { start } = this.visit;
    const view = cover(width, height);
    const [x, y, sx, sy] = view;
    const bank = this.placement.clear ? placeBank(this.visit.bank, boxesIn(host, view, this.placement.clear)) : this.visit.bank;
    gl.uniform4f(uniforms.view, x, y, sx, sy);
    gl.uniform2f(uniforms.wind, wrap(start[0] + WIND[0] * seconds), wrap(start[1] + WIND[1] * seconds));
    gl.uniform2f(uniforms.curl, wrap(start[2] + CURL[0] * seconds), wrap(start[3] + CURL[1] * seconds));
    gl.uniform2f(uniforms.wisp, wrap(start[4] + WISP[0] * seconds), wrap(start[5] + WISP[1] * seconds));
    gl.uniform4f(uniforms.bank, bank[0], bank[1], bank[2], bank[3]);
    gl.uniform1f(uniforms.shimmer, ((seconds / SHIMMER_SECONDS) % 1) * Math.PI * 2);
    gl.uniform1f(uniforms.night, this.appearance === "night" ? 1 : 0);
    gl.uniform2f(uniforms.size, w, h);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    if (!this.live) {
      this.live = true;
      // Settle the transparent style first, so the canvas fades in over the photo instead of snapping in.
      void getComputedStyle(canvas).opacity;
      canvas.classList.add("live");
    }
  }
}

/** One page's sky, which every host it lends to shares. */
export interface PageSky {
  /**
   * Puts the sky in `host`, filling it; `onFail` runs if it cannot be shown there. A host more than a screen from the
   * window gets its sky once it comes that near. Returns the host's release.
   */
  borrow(host: HTMLElement, onFail?: () => void): () => void;
  /** Changes the same sky in place, preserving this visit's clouds. New pages start in day. */
  setAppearance(appearance: SkyAppearance): void;
}

/**
 * A page's sky, built the first time a host on or near the screen asks for it. Make one for each page load, and one for
 * each part of a page that shows a sky of its own alongside the others.
 */
export function pageSky(placement: Placement = EITHER): PageSky {
  /** The sky the page's hosts share; null while no host shows it. */
  let shared: Sky | null = null;
  /** This page load's sky: drawn the first time a sky is built, and kept until the page goes. */
  let visit: Visit | null = null;
  let appearance: SkyAppearance = "day";
  const gone = (sky: Sky): void => {
    if (shared === sky) shared = null;
  };

  function build(): Sky | null {
    const canvas = document.createElement("canvas");
    try {
      const gl = canvas.getContext("webgl", CONTEXT);
      return gl ? new Sky(canvas, gl, (visit ??= roll(placement)), placement, gone, appearance) : null;
    } catch {
      return null;
    }
  }

  function lend(host: HTMLElement, onFail?: () => void): () => void {
    shared ??= build();
    const sky = shared;
    if (!sky) {
      onFail?.();
      return () => undefined;
    }
    const owner = sky.attach(host, onFail);
    return () => sky.detach(owner);
  }

  return {
    setAppearance(next) {
      if (appearance === next) return;
      appearance = next;
      shared?.setAppearance(next);
    },
    borrow(host, onFail) {
      // A sky already showing moves straight to the next host, and one on or near the screen is built at once.
      if (shared || typeof IntersectionObserver !== "function" || onScreen(host, NEAR)) return lend(host, onFail);
      // One further down the page waits until its host comes that near.
      let release: (() => void) | null = null;
      const near = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          near.disconnect();
          release = lend(host, onFail);
        },
        { rootMargin: `${NEAR * 100}% 0px` },
      );
      near.observe(host);
      return () => {
        near.disconnect();
        release?.();
      };
    },
  };
}
